/**
 * What a session's agent is given besides its own config (§5.26) — the second
 * of the twenty-odd settings that is not a value but a **document**, and for
 * the same reason the first one is: three lists of records do not go in a 288px
 * column, and the last time a setting shaped like this had to fit in one it
 * ended up as JSON in a textarea.
 *
 * So the row it lives on has no value column at all, and this is the value.
 *
 * **The grammar is the app's own** — `@airlock/contracts/settings/extensions`,
 * the same validators a member's machine and the Worker use — so what is
 * refused here is exactly what would be refused there, said in the same words,
 * beside the field that caused it rather than as a 422 after a publish.
 *
 * The one refusal worth reading twice is the credential: this document is
 * published to an organization, pulled onto every member's machine and read in
 * a browser. A server that needs a token reads it from the workspace's own
 * secrets, which are sealed and never travel in the clear.
 */
import type React from 'react';
import {
  EMPTY_EXTENSIONS,
  MAX_MCP_SERVERS,
  MAX_PLUGINS,
  MAX_SKILLS,
  mcpProblem,
  pluginProblem,
  skillProblem,
  type AgentExtensions,
  type McpServerEntry,
  type PluginEntry,
  type SkillEntry
} from '@airlock/contracts/settings/extensions';
import { color, font, radius } from '../../theme/tokens';
import { GhostButton, Input, RowAction } from '../../components/primitives';

const mono: React.CSSProperties = { fontFamily: font.mono, fontSize: 12.5 };

/** One list's heading, what it is for, and the way to add to it. */
function Block({
  label,
  blurb,
  editable,
  full,
  onAdd,
  children
}: {
  label: string;
  blurb: string;
  editable: boolean;
  full: boolean;
  onAdd: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h4 style={{ margin: 0, fontSize: 12, letterSpacing: '.07em', textTransform: 'uppercase', color: color.inkGhost }}>
          {label}
        </h4>
        <span style={{ flex: 1 }} />
        {editable && !full && (
          <GhostButton size="sm" onClick={onAdd}>
            Add
          </GhostButton>
        )}
      </div>
      <p style={{ margin: '5px 0 6px', maxWidth: 620, fontSize: 12, lineHeight: 1.45, color: color.inkFaint }}>
        {blurb}
      </p>
      {children}
    </section>
  );
}

/** One row of any of the three: its switch, its fields, its remove, its refusal. */
function Entry({
  enabled,
  onEnabled,
  problem,
  onRemove,
  editable,
  children
}: {
  enabled: boolean;
  onEnabled: (next: boolean) => void;
  problem: string | null;
  onRemove: () => void;
  editable: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={{ padding: '9px 0', boxShadow: `inset 0 -1px 0 ${color.hairSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={!editable}
          onClick={() => onEnabled(!enabled)}
          title={enabled ? 'On — a session gets it' : 'Off — kept here, not given to a session'}
          style={{
            position: 'relative',
            flex: 'none',
            width: 32,
            height: 19,
            padding: 0,
            border: `1px solid ${enabled ? color.ink : color.lineInput}`,
            borderRadius: radius.chip,
            background: enabled ? color.ink : 'transparent',
            cursor: editable ? 'pointer' : 'default'
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: enabled ? 15 : 2,
              width: 13,
              height: 13,
              borderRadius: radius.chip,
              background: enabled ? '#FFFFFF' : color.inkGhost
            }}
          />
        </button>
        {children}
        {editable && (
          <RowAction danger onClick={onRemove}>
            Remove
          </RowAction>
        )}
      </div>
      {problem && <p style={{ margin: '6px 0 0 42px', fontSize: 12, color: color.badText }}>{problem}</p>}
    </div>
  );
}

function Nothing(): React.JSX.Element {
  return <p style={{ margin: '4px 0 0', fontSize: 12.5, color: color.inkFaint }}>Nothing listed.</p>;
}

export function ExtensionsField({
  value,
  onChange,
  editable = true
}: {
  value: AgentExtensions | null | undefined;
  onChange: (next: AgentExtensions) => void;
  /** A reader, or a row an organization has locked: the lists stay readable. */
  editable?: boolean;
}): React.JSX.Element {
  const doc = value ?? EMPTY_EXTENSIONS;
  const patch = (part: Partial<AgentExtensions>): void => onChange({ ...doc, ...part });
  const replace = <T,>(list: T[], at: number, next: T): T[] => list.map((one, i) => (i === at ? next : one));
  const field = { ...mono, flex: 1, minWidth: 0 } as React.CSSProperties;

  return (
    <div>
      <Block
        label="Skills"
        blurb="A folder of the session's own workspace, given to the agent under a name it can call. The repositories are cloned into the session, so a skill a team keeps in one of them is already there — this is what makes it visible from the workspace root, where the agent runs."
        editable={editable}
        full={doc.skills.length >= MAX_SKILLS}
        onAdd={() => patch({ skills: [...doc.skills, { name: '', path: '', enabled: true }] })}
      >
        {doc.skills.map((skill: SkillEntry, at: number) => (
          <Entry
            key={at}
            editable={editable}
            enabled={skill.enabled}
            onEnabled={(enabled) => patch({ skills: replace(doc.skills, at, { ...skill, enabled }) })}
            problem={skillProblem(skill)}
            onRemove={() => patch({ skills: doc.skills.filter((_, i) => i !== at) })}
          >
            <Input
              value={skill.name}
              placeholder="name"
              disabled={!editable}
              onChange={(event) => patch({ skills: replace(doc.skills, at, { ...skill, name: event.target.value }) })}
              style={{ ...field, maxWidth: 200 }}
            />
            <Input
              value={skill.path}
              placeholder="tools/skills/review"
              disabled={!editable}
              onChange={(event) => patch({ skills: replace(doc.skills, at, { ...skill, path: event.target.value }) })}
              style={field}
            />
          </Entry>
        ))}
        {!doc.skills.length && <Nothing />}
      </Block>

      <Block
        label="MCP servers"
        blurb="A tool server the agent may call, started inside the session by the command named here. It runs in the sandbox like everything else, so it reaches what that session reaches and nothing more. A token does not go in it — this document is published, and a server that needs one reads it from the workspace's secrets."
        editable={editable}
        full={doc.mcpServers.length >= MAX_MCP_SERVERS}
        onAdd={() =>
          patch({ mcpServers: [...doc.mcpServers, { name: '', command: '', args: [], env: [], enabled: true }] })
        }
      >
        {doc.mcpServers.map((server: McpServerEntry, at: number) => (
          <Entry
            key={at}
            editable={editable}
            enabled={server.enabled}
            onEnabled={(enabled) => patch({ mcpServers: replace(doc.mcpServers, at, { ...server, enabled }) })}
            problem={mcpProblem(server)}
            onRemove={() => patch({ mcpServers: doc.mcpServers.filter((_, i) => i !== at) })}
          >
            <Input
              value={server.name}
              placeholder="name"
              disabled={!editable}
              onChange={(event) =>
                patch({ mcpServers: replace(doc.mcpServers, at, { ...server, name: event.target.value }) })
              }
              style={{ ...field, maxWidth: 180 }}
            />
            {/* Written as one line and read back as a command and its arguments:
                a row of the agent's config is a command line, and asking for it
                as two fields makes the reader do the split. */}
            <Input
              value={[server.command, ...server.args].join(' ')}
              placeholder="mcp-server --root ."
              disabled={!editable}
              onChange={(event) => {
                const [command = '', ...args] = event.target.value.split(/\s+/).filter(Boolean);
                patch({ mcpServers: replace(doc.mcpServers, at, { ...server, command, args }) });
              }}
              style={field}
            />
          </Entry>
        ))}
        {!doc.mcpServers.length && <Nothing />}
      </Block>

      <Block
        label="Plugins"
        blurb="A plugin the agent loads, and the marketplace it comes from. The marketplace is registered in the session and the plugin turned on there — the same two facts the agent's own /plugin writes."
        editable={editable}
        full={doc.plugins.length >= MAX_PLUGINS}
        onAdd={() => patch({ plugins: [...doc.plugins, { name: '', marketplace: '', source: '', enabled: true }] })}
      >
        {doc.plugins.map((plugin: PluginEntry, at: number) => (
          <Entry
            key={at}
            editable={editable}
            enabled={plugin.enabled}
            onEnabled={(enabled) => patch({ plugins: replace(doc.plugins, at, { ...plugin, enabled }) })}
            problem={pluginProblem(plugin)}
            onRemove={() => patch({ plugins: doc.plugins.filter((_, i) => i !== at) })}
          >
            <Input
              value={plugin.name}
              placeholder="plugin"
              disabled={!editable}
              onChange={(event) => patch({ plugins: replace(doc.plugins, at, { ...plugin, name: event.target.value }) })}
              style={{ ...field, maxWidth: 170 }}
            />
            <Input
              value={plugin.marketplace}
              placeholder="marketplace"
              disabled={!editable}
              onChange={(event) =>
                patch({ plugins: replace(doc.plugins, at, { ...plugin, marketplace: event.target.value }) })
              }
              style={{ ...field, maxWidth: 170 }}
            />
            <Input
              value={plugin.source}
              placeholder="owner/repo"
              disabled={!editable}
              onChange={(event) =>
                patch({ plugins: replace(doc.plugins, at, { ...plugin, source: event.target.value }) })
              }
              style={field}
            />
          </Entry>
        ))}
        {!doc.plugins.length && <Nothing />}
      </Block>
    </div>
  );
}
